<template>
  <div>
    <div
      class="flex items-center rounded-md border border-transparent transition-colors"
      :class="[
        active ? 'bg-primary-100' : 'hover:bg-gray-100',
        dropPos === 'inside' ? 'border-primary-500 bg-primary-50' : '',
        dropPos === 'before' ? 'border-t-2 border-t-primary-500' : '',
        dropPos === 'after' ? 'border-b-2 border-b-primary-500' : '',
      ]"
      :style="{ paddingLeft: `${depth * 14}px` }"
      :draggable="canDrag && !!node.draggable"
      @dragstart.stop="onDragStart"
      @dragover.prevent.stop="onDragOver"
      @dragleave.stop="dropPos = ''"
      @drop.prevent.stop="onDrop"
    >
      <button v-if="hasChildren" type="button" class="w-7 py-2 text-gray-500 text-[10px] shrink-0" @click="$emit('toggle', node.key)">
        <font-awesome-icon :icon="['fas', open ? 'chevron-down' : 'chevron-right']" />
      </button>
      <span v-else class="w-7 shrink-0"></span>
      <router-link v-if="node.to" :to="node.to" class="flex flex-1 items-center gap-2 min-w-0 py-1.5 pr-2 no-underline text-gray-800">
        <font-awesome-icon v-if="node.icon" :icon="node.icon" :title="node.hint" :class="['text-xs shrink-0', node.tone || 'text-gray-400']" />
        <span class="truncate text-[13px]" :class="['font-medium', { sensitive: node.sensitive }]" data-spoof-kind="label">{{ node.label }}</span>
      </router-link>
    </div>
    <template v-if="hasChildren && open">
      <PagesTreeNode
        v-for="child in node.children" :key="child.key"
        :node="child" :depth="depth + 1" :expanded="expanded" :active-path="activePath" :can-drag="canDrag"
        @toggle="$emit('toggle', $event)"
        @node-drop="$emit('node-drop', $event)"
      />
    </template>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'PagesTreeNode',
  components: { FontAwesomeIcon },
  props: {
    node: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    expanded: { type: Object, required: true },
    activePath: { type: String, default: '' },
    canDrag: { type: Boolean, default: false },
  },
  emits: ['toggle', 'node-drop'],
  data() {
    return { dropPos: '' };
  },
  computed: {
    hasChildren() {
      return !!(this.node.children && this.node.children.length);
    },
    open() {
      return !!this.expanded[this.node.key];
    },
    active() {
      return !!this.node.to && this.$router.resolve(this.node.to).path === this.activePath;
    },
  },
  methods: {
    onDragStart(e) {
      if (!this.canDrag || !this.node.draggable) return;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/json', JSON.stringify({
        kind: this.node.kind,
        pageId: this.node.pageId || null,
        stem: this.node.stem || '',
        parentStem: this.node.parentStem || '',
        key: this.node.key,
      }));
    },
    onDragOver(e) {
      if (!this.canDrag || this.node.kind === 'metric') return;
      const rect = e.currentTarget.getBoundingClientRect();
      const rel = (e.clientY - rect.top) / (rect.height || 1);
      if (this.node.kind === 'folder' && rel > 0.25 && rel < 0.75) {
        this.dropPos = 'inside';
      } else {
        this.dropPos = rel < 0.5 ? 'before' : 'after';
      }
    },
    onDrop(e) {
      const pos = this.dropPos || 'inside';
      this.dropPos = '';
      if (!this.canDrag) return;
      try {
        const source = JSON.parse(e.dataTransfer.getData('application/json') || '{}');
        if (!source.key || source.key === this.node.key) return;
        this.$emit('node-drop', { source, target: this.node, position: pos });
      } catch {
        // ignore invalid drag payload
      }
    },
  },
};
</script>
